import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  SelectChangeEvent,
  Slider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh,
  Add,
  EmojiEvents,
  WarningAmber,
  Flag,
  RateReview,
  CalendarMonth,
  TrendingUp,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import hrPlusService from '../../services/hrPlusService';
import hrService, { EmployeeListItem } from '../../services/hrService';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Types — the API layer is untyped (Promise<any>); these mirror the actual
// column names returned by HrTalent.js on the backend, so the UI stays honest
// about what fields really exist (e.g. progress/target are free-form, not
// enums).
// ---------------------------------------------------------------------------

type CycleStatus = 'DRAFT' | 'ACTIVE' | 'REVIEW' | 'CLOSED';
interface Cycle {
  id: number;
  name: string;
  period_start: string | null;
  period_end: string | null;
  status: CycleStatus;
  created_at?: string;
}

type GoalStatus = 'DRAFT' | 'ACTIVE' | 'ACHIEVED' | 'MISSED' | 'CANCELLED';
interface Goal {
  id: number;
  employee_id: number;
  employee_name: string;
  cycle_id: number | null;
  cycle_name: string | null;
  title: string;
  description: string | null;
  category: string | null;
  weight: number | string | null;
  target: string | null;
  progress: number | string;
  status: GoalStatus;
  created_at: string;
}

type ReviewStatus = 'PENDING' | 'SELF_DONE' | 'MANAGER_DONE' | 'FINALIZED';
interface Review {
  id: number;
  employee_id: number;
  employee_name: string;
  department_id: number | null;
  cycle_id: number | null;
  cycle_name: string | null;
  reviewer_id: number | null;
  reviewer_name: string | null;
  self_rating: number | string | null;
  manager_rating: number | string | null;
  final_rating: number | string | null;
  comments: string | null;
  promotion_recommended: number | boolean | null;
  increment_recommended: number | boolean | null;
  status: ReviewStatus;
  created_at: string;
}

interface PerformanceDashboard {
  goalCompletionPct: number | null;
  goalsByStatus: Record<string, number>;
  avgFinalRatingByDepartment: { deptName: string; avgFinalRating: number | null; reviewCount: number }[];
  reviewsByStatus: Record<string, number>;
  topPerformers: { employeeId: number; employeeName: string; finalRating: number; deptName: string | null }[];
  employeesNeedingAttention: {
    employeeId: number;
    employeeName: string;
    deptName: string | null;
    finalRating: number | null;
    missedGoals: number;
    reason: string;
  }[];
}

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');
const fmtRating = (v: number | string | null | undefined) => {
  const n = num(v);
  return n === null ? '—' : n.toFixed(1);
};

const cycleStatuses: CycleStatus[] = ['DRAFT', 'ACTIVE', 'REVIEW', 'CLOSED'];
const cycleStatusTone: Record<CycleStatus, StatusTone> = { DRAFT: 'neutral', ACTIVE: 'info', REVIEW: 'warning', CLOSED: 'success' };

const goalStatuses: GoalStatus[] = ['DRAFT', 'ACTIVE', 'ACHIEVED', 'MISSED', 'CANCELLED'];
const goalStatusTone: Record<GoalStatus, StatusTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'info',
  ACHIEVED: 'success',
  MISSED: 'error',
  CANCELLED: 'neutral',
};
const goalStatusLabel: Record<GoalStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  ACHIEVED: 'Achieved',
  MISSED: 'Missed',
  CANCELLED: 'Cancelled',
};

const reviewStatuses: ReviewStatus[] = ['PENDING', 'SELF_DONE', 'MANAGER_DONE', 'FINALIZED'];
const reviewStatusTone: Record<ReviewStatus, StatusTone> = {
  PENDING: 'neutral',
  SELF_DONE: 'info',
  MANAGER_DONE: 'warning',
  FINALIZED: 'success',
};
const reviewStatusLabel: Record<ReviewStatus, string> = {
  PENDING: 'Pending',
  SELF_DONE: 'Self Done',
  MANAGER_DONE: 'Manager Done',
  FINALIZED: 'Finalized',
};

const attentionReasonLabel: Record<string, string> = {
  LOW_RATING: 'Low rating',
  MISSED_GOALS: 'Missed goals',
  LOW_RATING_AND_MISSED_GOALS: 'Low rating + missed goals',
};

// ---------------------------------------------------------------------------
// Small local building blocks
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: subtitle ? 0.25 : 1.5, gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

const SectionHeader: React.FC<{ title: string; onRefresh: () => void; busy: boolean; action?: React.ReactNode }> = ({
  title,
  onRefresh,
  busy,
  action,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh} disabled={busy}>
          <Refresh />
        </IconButton>
      </Tooltip>
      {action}
    </Box>
  </Box>
);

/** Inline progress editor for a goal row — drags locally, commits on release. */
const GoalProgressCell: React.FC<{ goal: Goal; onCommit: (id: number, value: number) => void; busy: boolean }> = ({
  goal,
  onCommit,
  busy,
}) => {
  const initial = num(goal.progress) ?? 0;
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal.id, goal.progress]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
      <Slider
        size="small"
        value={value}
        min={0}
        max={100}
        step={5}
        disabled={busy}
        onChange={(_e, v) => setValue(Array.isArray(v) ? v[0] : v)}
        onChangeCommitted={(_e, v) => onCommit(goal.id, Array.isArray(v) ? v[0] : v)}
        sx={{ width: 96 }}
      />
      <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>
        {value}%
      </Typography>
    </Box>
  );
};

type TabKey = 'dashboard' | 'cycles' | 'goals' | 'reviews';

const PerformancePage: React.FC = () => {
  const t = useChartTokens();
  const [tab, setTab] = useState<TabKey>('dashboard');

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);

  const [dashboard, setDashboard] = useState<PerformanceDashboard | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [cyclesLoading, setCyclesLoading] = useState(true);
  const [cyclesError, setCyclesError] = useState<string | null>(null);
  const [cycleStatusUpdating, setCycleStatusUpdating] = useState<number | null>(null);

  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [goalProgressUpdating, setGoalProgressUpdating] = useState<number | null>(null);
  const [goalCycleFilter, setGoalCycleFilter] = useState<number | ''>('');
  const [goalEmployeeFilter, setGoalEmployeeFilter] = useState<number | ''>('');
  const [goalStatusFilter, setGoalStatusFilter] = useState<GoalStatus | 'ALL'>('ALL');

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [reviewStatusUpdating, setReviewStatusUpdating] = useState<number | null>(null);
  const [reviewCycleFilter, setReviewCycleFilter] = useState<number | ''>('');
  const [reviewEmployeeFilter, setReviewEmployeeFilter] = useState<number | ''>('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState<ReviewStatus | 'ALL'>('ALL');

  // ---- dialogs: cycle ----
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [cycleName, setCycleName] = useState('');
  const [cyclePeriodStart, setCyclePeriodStart] = useState('');
  const [cyclePeriodEnd, setCyclePeriodEnd] = useState('');
  const [cycleBusy, setCycleBusy] = useState(false);
  const [cycleFormError, setCycleFormError] = useState<string | null>(null);

  // ---- dialogs: goal ----
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalEmployeeId, setGoalEmployeeId] = useState<number | ''>('');
  const [goalCycleId, setGoalCycleId] = useState<number | ''>('');
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalCategory, setGoalCategory] = useState('');
  const [goalWeight, setGoalWeight] = useState('');
  const [goalTarget, setGoalTarget] = useState('');
  const [goalBusy, setGoalBusy] = useState(false);
  const [goalFormError, setGoalFormError] = useState<string | null>(null);

  // ---- dialogs: review ----
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewEmployeeId, setReviewEmployeeId] = useState<number | ''>('');
  const [reviewCycleId, setReviewCycleId] = useState<number | ''>('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewFormError, setReviewFormError] = useState<string | null>(null);

  // ---- dialogs: edit review ratings ----
  const [editReview, setEditReview] = useState<Review | null>(null);
  const [editSelfRating, setEditSelfRating] = useState('');
  const [editManagerRating, setEditManagerRating] = useState('');
  const [editFinalRating, setEditFinalRating] = useState('');
  const [editComments, setEditComments] = useState('');
  const [editPromotion, setEditPromotion] = useState(false);
  const [editIncrement, setEditIncrement] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editFormError, setEditFormError] = useState<string | null>(null);

  const loadDashboard = () => {
    setDashboardLoading(true);
    setDashboardError(null);
    hrPlusService
      .performanceDashboard()
      .then((res: PerformanceDashboard) => setDashboard(res))
      .catch((err: any) => setDashboardError(err?.message || 'Failed to load performance dashboard'))
      .finally(() => setDashboardLoading(false));
  };

  const loadCycles = () => {
    setCyclesLoading(true);
    setCyclesError(null);
    hrPlusService
      .getCycles()
      .then((res: { cycles: Cycle[] }) => setCycles(res.cycles))
      .catch((err: any) => setCyclesError(err?.message || 'Failed to load performance cycles'))
      .finally(() => setCyclesLoading(false));
  };

  const loadGoals = () => {
    setGoalsLoading(true);
    setGoalsError(null);
    hrPlusService
      .getGoals()
      .then((res: { goals: Goal[] }) => setGoals(res.goals))
      .catch((err: any) => setGoalsError(err?.message || 'Failed to load goals'))
      .finally(() => setGoalsLoading(false));
  };

  const loadReviews = () => {
    setReviewsLoading(true);
    setReviewsError(null);
    hrPlusService
      .getReviews()
      .then((res: { reviews: Review[] }) => setReviews(res.reviews))
      .catch((err: any) => setReviewsError(err?.message || 'Failed to load reviews'))
      .finally(() => setReviewsLoading(false));
  };

  useEffect(() => {
    loadDashboard();
    loadCycles();
    loadGoals();
    loadReviews();
    hrService
      .getEmployees({ status: 'ACTIVE', limit: 200 })
      .then((res) => setEmployees(res.employees))
      .catch(() => undefined);
  }, []);

  const employeeLabel = (e: EmployeeListItem) => `${e.employee_code} — ${e.first_name}${e.last_name ? ` ${e.last_name}` : ''}`;

  const filteredGoals = useMemo(
    () =>
      goals.filter((g) => {
        if (goalCycleFilter !== '' && g.cycle_id !== goalCycleFilter) return false;
        if (goalEmployeeFilter !== '' && g.employee_id !== goalEmployeeFilter) return false;
        if (goalStatusFilter !== 'ALL' && g.status !== goalStatusFilter) return false;
        return true;
      }),
    [goals, goalCycleFilter, goalEmployeeFilter, goalStatusFilter]
  );

  const filteredReviews = useMemo(
    () =>
      reviews.filter((r) => {
        if (reviewCycleFilter !== '' && r.cycle_id !== reviewCycleFilter) return false;
        if (reviewEmployeeFilter !== '' && r.employee_id !== reviewEmployeeFilter) return false;
        if (reviewStatusFilter !== 'ALL' && r.status !== reviewStatusFilter) return false;
        return true;
      }),
    [reviews, reviewCycleFilter, reviewEmployeeFilter, reviewStatusFilter]
  );

  const goalStatusCounts = useMemo(() => {
    const c: Record<GoalStatus, number> = { DRAFT: 0, ACTIVE: 0, ACHIEVED: 0, MISSED: 0, CANCELLED: 0 };
    goals.forEach((g) => {
      if (c[g.status] !== undefined) c[g.status] += 1;
    });
    return c;
  }, [goals]);

  const reviewStatusCounts = useMemo(() => {
    const c: Record<ReviewStatus, number> = { PENDING: 0, SELF_DONE: 0, MANAGER_DONE: 0, FINALIZED: 0 };
    reviews.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status] += 1;
    });
    return c;
  }, [reviews]);

  // ---- dashboard derived values ----
  const reviewCompletionPct = useMemo(() => {
    if (!dashboard) return null;
    const total = Object.values(dashboard.reviewsByStatus).reduce((s, v) => s + v, 0);
    if (total === 0) return null;
    const finalized = dashboard.reviewsByStatus.FINALIZED || 0;
    return Math.round((finalized / total) * 1000) / 10;
  }, [dashboard]);

  const goalDonutData = useMemo(() => {
    if (!dashboard) return [];
    return goalStatuses
      .filter((s) => (dashboard.goalsByStatus[s] || 0) > 0)
      .map((s, _i) => ({ status: s, label: goalStatusLabel[s], value: dashboard.goalsByStatus[s], color: t.series(goalStatuses.indexOf(s)) }));
  }, [dashboard, t]);

  const totalGoalsFromStatus = useMemo(() => {
    if (!dashboard) return 0;
    return Object.values(dashboard.goalsByStatus).reduce((s, v) => s + v, 0);
  }, [dashboard]);

  // ---- cycle handlers ----
  const openCycleDialog = () => {
    setCycleName('');
    setCyclePeriodStart('');
    setCyclePeriodEnd('');
    setCycleFormError(null);
    setCycleDialogOpen(true);
  };

  const handleCreateCycle = async () => {
    if (!cycleName.trim()) {
      setCycleFormError('Cycle name is required');
      return;
    }
    setCycleBusy(true);
    setCycleFormError(null);
    try {
      await hrPlusService.createCycle({
        name: cycleName,
        periodStart: cyclePeriodStart || undefined,
        periodEnd: cyclePeriodEnd || undefined,
      });
      setCycleDialogOpen(false);
      loadCycles();
    } catch (err: any) {
      setCycleFormError(err?.message || 'Failed to create cycle');
    } finally {
      setCycleBusy(false);
    }
  };

  const handleCycleStatusChange = async (cycle: Cycle, e: SelectChangeEvent) => {
    const status = e.target.value as CycleStatus;
    if (status === cycle.status) return;
    setCycleStatusUpdating(cycle.id);
    try {
      const res: { cycle: Cycle } = await hrPlusService.setCycleStatus(cycle.id, status);
      setCycles((prev) => prev.map((c) => (c.id === cycle.id ? res.cycle : c)));
    } catch (err: any) {
      setCyclesError(err?.message || 'Failed to update cycle status');
    } finally {
      setCycleStatusUpdating(null);
    }
  };

  // ---- goal handlers ----
  const openGoalDialog = () => {
    setGoalEmployeeId('');
    setGoalCycleId('');
    setGoalTitle('');
    setGoalDescription('');
    setGoalCategory('');
    setGoalWeight('');
    setGoalTarget('');
    setGoalFormError(null);
    setGoalDialogOpen(true);
  };

  const handleCreateGoal = async () => {
    if (!goalEmployeeId || !goalTitle.trim()) {
      setGoalFormError('Employee and title are required');
      return;
    }
    setGoalBusy(true);
    setGoalFormError(null);
    try {
      await hrPlusService.createGoal({
        employeeId: goalEmployeeId,
        cycleId: goalCycleId || undefined,
        title: goalTitle,
        description: goalDescription || undefined,
        category: goalCategory || undefined,
        weight: goalWeight ? Number(goalWeight) : undefined,
        target: goalTarget || undefined,
      });
      setGoalDialogOpen(false);
      loadGoals();
    } catch (err: any) {
      setGoalFormError(err?.message || 'Failed to create goal');
    } finally {
      setGoalBusy(false);
    }
  };

  const handleGoalProgressCommit = async (id: number, value: number) => {
    setGoalProgressUpdating(id);
    try {
      const res: { goal: Goal } = await hrPlusService.setGoalProgress(id, value);
      setGoals((prev) => prev.map((g) => (g.id === id ? res.goal : g)));
    } catch (err: any) {
      setGoalsError(err?.message || 'Failed to update goal progress');
    } finally {
      setGoalProgressUpdating(null);
    }
  };

  // ---- review handlers ----
  const openReviewDialog = () => {
    setReviewEmployeeId('');
    setReviewCycleId('');
    setReviewFormError(null);
    setReviewDialogOpen(true);
  };

  const handleCreateReview = async () => {
    if (!reviewEmployeeId) {
      setReviewFormError('Employee is required');
      return;
    }
    setReviewBusy(true);
    setReviewFormError(null);
    try {
      await hrPlusService.createReview({ employeeId: reviewEmployeeId, cycleId: reviewCycleId || undefined });
      setReviewDialogOpen(false);
      loadReviews();
    } catch (err: any) {
      setReviewFormError(err?.message || 'Failed to create review');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleReviewStatusChange = async (review: Review, e: SelectChangeEvent) => {
    const status = e.target.value as ReviewStatus;
    if (status === review.status) return;
    setReviewStatusUpdating(review.id);
    try {
      // hrPlusService has no dedicated review-status helper (unlike cycles);
      // the backend route exists (PATCH /hr-talent/reviews/:id/status), so
      // call it directly rather than editing the shared service module.
      const res: { review: Review } = await api.patch(`/hr-talent/reviews/${review.id}/status`, { status });
      setReviews((prev) => prev.map((r) => (r.id === review.id ? res.review : r)));
    } catch (err: any) {
      setReviewsError(err?.message || 'Failed to update review status');
    } finally {
      setReviewStatusUpdating(null);
    }
  };

  const openEditReview = (review: Review) => {
    setEditReview(review);
    setEditSelfRating(review.self_rating !== null && review.self_rating !== undefined ? String(review.self_rating) : '');
    setEditManagerRating(review.manager_rating !== null && review.manager_rating !== undefined ? String(review.manager_rating) : '');
    setEditFinalRating(review.final_rating !== null && review.final_rating !== undefined ? String(review.final_rating) : '');
    setEditComments(review.comments || '');
    setEditPromotion(!!review.promotion_recommended);
    setEditIncrement(!!review.increment_recommended);
    setEditFormError(null);
  };

  const handleSaveReview = async () => {
    if (!editReview) return;
    setEditBusy(true);
    setEditFormError(null);
    try {
      const res: { review: Review } = await hrPlusService.updateReview(editReview.id, {
        selfRating: editSelfRating ? Number(editSelfRating) : undefined,
        managerRating: editManagerRating ? Number(editManagerRating) : undefined,
        finalRating: editFinalRating ? Number(editFinalRating) : undefined,
        comments: editComments || undefined,
        promotionRecommended: editPromotion,
        incrementRecommended: editIncrement,
      });
      setReviews((prev) => prev.map((r) => (r.id === editReview.id ? res.review : r)));
      setEditReview(null);
    } catch (err: any) {
      setEditFormError(err?.message || 'Failed to save review');
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Performance Management" subtitle="Goals, review cycles and rating outcomes across the organisation" />

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab value="dashboard" label="Dashboard" />
        <Tab value="cycles" label="Cycles" />
        <Tab value="goals" label="Goals" />
        <Tab value="reviews" label="Reviews" />
      </Tabs>

      {/* ---------------------------------------------------------------- */}
      {/* DASHBOARD                                                        */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'dashboard' && (
        <Box>
          {dashboardError && !dashboardLoading ? (
            <ErrorState message={dashboardError} onRetry={loadDashboard} />
          ) : dashboardLoading && !dashboard ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    title="Goal Completion"
                    value={dashboard?.goalCompletionPct != null ? `${dashboard.goalCompletionPct}%` : null}
                    icon={<Flag />}
                    color="#0d9488"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    title="Review Completion"
                    value={reviewCompletionPct != null ? `${reviewCompletionPct}%` : null}
                    icon={<RateReview />}
                    color="#0284c7"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    title="Achieved Goals"
                    value={dashboard?.goalsByStatus.ACHIEVED ?? 0}
                    icon={<TrendingUp />}
                    color="#059669"
                    share={totalGoalsFromStatus > 0 ? (dashboard?.goalsByStatus.ACHIEVED ?? 0) / totalGoalsFromStatus : undefined}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    title="Needing Attention"
                    value={dashboard?.employeesNeedingAttention.length ?? 0}
                    icon={<WarningAmber />}
                    color="#dc2626"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <SectionCard title="Average Final Rating by Department" subtitle="Finalised reviews only, scale 1–5">
                    {!dashboard || dashboard.avgFinalRatingByDepartment.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No finalised reviews yet.
                      </Typography>
                    ) : (
                      <Box sx={{ height: Math.max(200, dashboard.avgFinalRatingByDepartment.length * 40) }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={dashboard.avgFinalRatingByDepartment}
                            layout="vertical"
                            margin={{ top: 4, right: 24, left: 4, bottom: 4 }}
                          >
                            <CartesianGrid {...gridProps(t)} horizontal={false} />
                            <XAxis type="number" domain={[0, 5]} {...axisProps(t)} />
                            <YAxis type="category" dataKey="deptName" {...axisProps(t)} width={130} tick={{ fill: t.textSecondary, fontSize: 11 }} />
                            <RTooltip
                              {...tooltipStyle(t)}
                              formatter={(v: number, _n, p: any) => [`${Number(v).toFixed(2)} (${p?.payload?.reviewCount ?? 0} reviews)`, 'Avg rating']}
                            />
                            <Bar dataKey="avgFinalRating" radius={[0, 3, 3, 0]} maxBarSize={22}>
                              {dashboard.avgFinalRatingByDepartment.map((_d, i) => (
                                <Cell key={i} fill={t.series(i)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </SectionCard>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <SectionCard title="Goals by Status" subtitle="All cycles">
                    {goalDonutData.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No goals recorded yet.
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ height: 200 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={goalDonutData} dataKey="value" nameKey="label" innerRadius={52} outerRadius={78} paddingAngle={2} strokeWidth={2} stroke={t.surface}>
                                {goalDonutData.map((d) => (
                                  <Cell key={d.status} fill={d.color} />
                                ))}
                              </Pie>
                              <RTooltip {...tooltipStyle(t)} formatter={(v: number, n: any) => [v, n]} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {goalDonutData.map((d) => (
                            <Box key={d.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: d.color }} />
                              <Typography variant="caption" color="text.secondary">
                                {d.label} ({d.value})
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>

              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard title="Top Performers" subtitle="Highest finalised ratings">
                    <TableSurface>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Employee', 'Department', 'Rating'].map((h, i) => (
                                <TableCell key={h} align={i === 2 ? 'right' : 'left'} sx={headerCellSx}>
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!dashboard || dashboard.topPerformers.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={3} sx={{ border: 0 }}>
                                  <TableEmptyState title="No rated reviews yet" />
                                </TableCell>
                              </TableRow>
                            ) : (
                              dashboard.topPerformers.map((p) => (
                                <TableRow key={p.employeeId} sx={bodyRowSx}>
                                  <TableCell sx={{ py: 1.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <EmojiEvents sx={{ fontSize: 16, color: '#d97706' }} />
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {p.employeeName}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{p.deptName || '—'}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                      {fmtRating(p.finalRating)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </TableSurface>
                  </SectionCard>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard title="Employees Needing Attention" subtitle="Low ratings or missed goals">
                    <TableSurface>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Employee', 'Rating', 'Missed Goals', 'Reason'].map((h, i) => (
                                <TableCell key={h} align={i === 1 || i === 2 ? 'right' : 'left'} sx={headerCellSx}>
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!dashboard || dashboard.employeesNeedingAttention.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ border: 0 }}>
                                  <TableEmptyState title="No one needs attention right now" />
                                </TableCell>
                              </TableRow>
                            ) : (
                              dashboard.employeesNeedingAttention.map((a) => (
                                <TableRow key={a.employeeId} sx={bodyRowSx}>
                                  <TableCell sx={{ py: 1.25 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {a.employeeName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {a.deptName || ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                      {fmtRating(a.finalRating)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                      {a.missedGoals}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <StatusDot label={attentionReasonLabel[a.reason] || a.reason} tone="warning" />
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </TableSurface>
                  </SectionCard>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* CYCLES                                                           */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'cycles' && (
        <Box>
          <SectionHeader
            title="Performance Cycles"
            onRefresh={loadCycles}
            busy={cyclesLoading}
            action={
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openCycleDialog}>
                New Cycle
              </Button>
            }
          />

          {cyclesError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCyclesError(null)}>
              {cyclesError}
            </Alert>
          )}

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Name', 'Period Start', 'Period End', 'Status'].map((h) => (
                      <TableCell key={h} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cyclesLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : cycles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <TableEmptyState title="No performance cycles yet" hint="Create one to start collecting goals and reviews." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    cycles.map((c) => (
                      <TableRow key={c.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarMonth sx={{ fontSize: 17, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fmtDate(c.period_start)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fmtDate(c.period_end)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StatusDot label={c.status} tone={cycleStatusTone[c.status]} />
                            <Select
                              size="small"
                              value={c.status}
                              onChange={(e) => handleCycleStatusChange(c, e)}
                              disabled={cycleStatusUpdating === c.id}
                              sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' } }}
                            >
                              {cycleStatuses.map((s) => (
                                <MenuItem key={s} value={s}>
                                  Move to {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* GOALS                                                            */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'goals' && (
        <Box>
          <SectionHeader
            title="Goals"
            onRefresh={loadGoals}
            busy={goalsLoading}
            action={
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openGoalDialog}>
                New Goal
              </Button>
            }
          />

          <FilterBar>
            <Chip
              label={`All (${goals.length})`}
              size="small"
              color={goalStatusFilter === 'ALL' ? 'primary' : 'default'}
              variant={goalStatusFilter === 'ALL' ? 'filled' : 'outlined'}
              onClick={() => setGoalStatusFilter('ALL')}
            />
            {goalStatuses.map((s) => (
              <Chip
                key={s}
                size="small"
                label={`${goalStatusLabel[s]} (${goalStatusCounts[s]})`}
                color={goalStatusFilter === s ? 'primary' : 'default'}
                variant={goalStatusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setGoalStatusFilter(s)}
              />
            ))}
            <TextField
              select
              size="small"
              label="Cycle"
              value={goalCycleFilter}
              onChange={(e) => setGoalCycleFilter(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Cycles</MenuItem>
              {cycles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Employee"
              value={goalEmployeeFilter}
              onChange={(e) => setGoalEmployeeFilter(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {employeeLabel(e)}
                </MenuItem>
              ))}
            </TextField>
            <FilterSpacer />
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredGoals.length}</strong> goals
            </Typography>
          </FilterBar>

          {goalsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGoalsError(null)}>
              {goalsError}
            </Alert>
          )}

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Employee', 'Cycle', 'Title', 'Category', 'Weight', 'Target', 'Progress', 'Status'].map((h, i) => (
                      <TableCell key={h} align={i === 4 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {goalsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : filteredGoals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ border: 0 }}>
                        <TableEmptyState title="No goals match this filter" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGoals.map((g) => (
                      <TableRow key={g.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {g.employee_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{g.cycle_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{g.title}</Typography>
                        </TableCell>
                        <TableCell>{g.category ? <TypePill label={g.category} tint={t.categorical[0]} /> : <Typography variant="body2">—</Typography>}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {num(g.weight) !== null ? num(g.weight) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={g.target || undefined}>
                            {g.target || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <GoalProgressCell goal={g} onCommit={handleGoalProgressCommit} busy={goalProgressUpdating === g.id} />
                        </TableCell>
                        <TableCell>
                          <StatusDot label={goalStatusLabel[g.status]} tone={goalStatusTone[g.status]} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* REVIEWS                                                          */}
      {/* ---------------------------------------------------------------- */}
      {tab === 'reviews' && (
        <Box>
          <SectionHeader
            title="Reviews"
            onRefresh={loadReviews}
            busy={reviewsLoading}
            action={
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openReviewDialog}>
                New Review
              </Button>
            }
          />

          <FilterBar>
            <Chip
              label={`All (${reviews.length})`}
              size="small"
              color={reviewStatusFilter === 'ALL' ? 'primary' : 'default'}
              variant={reviewStatusFilter === 'ALL' ? 'filled' : 'outlined'}
              onClick={() => setReviewStatusFilter('ALL')}
            />
            {reviewStatuses.map((s) => (
              <Chip
                key={s}
                size="small"
                label={`${reviewStatusLabel[s]} (${reviewStatusCounts[s]})`}
                color={reviewStatusFilter === s ? 'primary' : 'default'}
                variant={reviewStatusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setReviewStatusFilter(s)}
              />
            ))}
            <TextField
              select
              size="small"
              label="Cycle"
              value={reviewCycleFilter}
              onChange={(e) => setReviewCycleFilter(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Cycles</MenuItem>
              {cycles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Employee"
              value={reviewEmployeeFilter}
              onChange={(e) => setReviewEmployeeFilter(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {employeeLabel(e)}
                </MenuItem>
              ))}
            </TextField>
            <FilterSpacer />
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredReviews.length}</strong> reviews
            </Typography>
          </FilterBar>

          {reviewsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewsError(null)}>
              {reviewsError}
            </Alert>
          )}

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Employee', 'Cycle', 'Reviewer', 'Self', 'Manager', 'Final', 'Recommendation', 'Status', ''].map((h, i) => (
                      <TableCell key={h || `col-${i}`} align={i >= 3 && i <= 5 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviewsLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : filteredReviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ border: 0 }}>
                        <TableEmptyState title="No reviews match this filter" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReviews.map((r) => (
                      <TableRow key={r.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.employee_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.cycle_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.reviewer_name || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {fmtRating(r.self_rating)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {fmtRating(r.manager_rating)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtRating(r.final_rating)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {r.promotion_recommended ? <TypePill label="Promotion" tint="#7c3aed" /> : null}
                            {r.increment_recommended ? <TypePill label="Increment" tint="#0d9488" /> : null}
                            {!r.promotion_recommended && !r.increment_recommended && (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StatusDot label={reviewStatusLabel[r.status]} tone={reviewStatusTone[r.status]} />
                            <Select
                              size="small"
                              value={r.status}
                              onChange={(e) => handleReviewStatusChange(r, e)}
                              disabled={reviewStatusUpdating === r.id}
                              sx={{ minWidth: 150, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' } }}
                            >
                              {reviewStatuses.map((s) => (
                                <MenuItem key={s} value={s}>
                                  Move to {reviewStatusLabel[s]}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openEditReview(r)}>
                            Edit Ratings
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      {/* New cycle dialog */}
      <Dialog open={cycleDialogOpen} onClose={() => (cycleBusy ? undefined : setCycleDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Performance Cycle</DialogTitle>
        <DialogContent dividers>
          {cycleFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cycleFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Name" required value={cycleName} onChange={(e) => setCycleName(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Period Start"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={cyclePeriodStart}
                onChange={(e) => setCyclePeriodStart(e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Period End"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={cyclePeriodEnd}
                onChange={(e) => setCyclePeriodEnd(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleDialogOpen(false)} disabled={cycleBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateCycle} disabled={cycleBusy}>
            {cycleBusy ? 'Creating…' : 'Create Cycle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New goal dialog */}
      <Dialog open={goalDialogOpen} onClose={() => (goalBusy ? undefined : setGoalDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Goal</DialogTitle>
        <DialogContent dividers>
          {goalFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {goalFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Employee"
                required
                value={goalEmployeeId}
                onChange={(e) => setGoalEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {employeeLabel(e)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Cycle"
                value={goalCycleId}
                onChange={(e) => setGoalCycleId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {cycles.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Title" required value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                minRows={2}
                value={goalDescription}
                onChange={(e) => setGoalDescription(e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth size="small" label="Category" value={goalCategory} onChange={(e) => setGoalCategory(e.target.value)} />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Weight (%)"
                type="number"
                value={goalWeight}
                onChange={(e) => setGoalWeight(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Target" value={goalTarget} onChange={(e) => setGoalTarget(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalDialogOpen(false)} disabled={goalBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateGoal} disabled={goalBusy}>
            {goalBusy ? 'Creating…' : 'Create Goal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New review dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => (reviewBusy ? undefined : setReviewDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>New Review</DialogTitle>
        <DialogContent dividers>
          {reviewFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reviewFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Employee"
                required
                value={reviewEmployeeId}
                onChange={(e) => setReviewEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {employeeLabel(e)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Cycle"
                value={reviewCycleId}
                onChange={(e) => setReviewCycleId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">—</MenuItem>
                {cycles.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={reviewBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateReview} disabled={reviewBusy}>
            {reviewBusy ? 'Creating…' : 'Create Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit review ratings dialog */}
      <Dialog open={!!editReview} onClose={() => (editBusy ? undefined : setEditReview(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Review — {editReview?.employee_name}</DialogTitle>
        <DialogContent dividers>
          {editFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid size={4}>
              <TextField
                fullWidth
                size="small"
                label="Self Rating"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.5 }}
                value={editSelfRating}
                onChange={(e) => setEditSelfRating(e.target.value)}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                size="small"
                label="Manager Rating"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.5 }}
                value={editManagerRating}
                onChange={(e) => setEditManagerRating(e.target.value)}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                size="small"
                label="Final Rating"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.5 }}
                value={editFinalRating}
                onChange={(e) => setEditFinalRating(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Comments"
                multiline
                minRows={3}
                value={editComments}
                onChange={(e) => setEditComments(e.target.value)}
              />
            </Grid>
            <Grid size={6}>
              <FormControlLabel
                control={<Switch checked={editPromotion} onChange={(e) => setEditPromotion(e.target.checked)} />}
                label="Recommend Promotion"
              />
            </Grid>
            <Grid size={6}>
              <FormControlLabel
                control={<Switch checked={editIncrement} onChange={(e) => setEditIncrement(e.target.checked)} />}
                label="Recommend Increment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditReview(null)} disabled={editBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveReview} disabled={editBusy}>
            {editBusy ? 'Saving…' : 'Save Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformancePage;
